/**
 * RiderPreSettlementHistory Helpers
 * 라이더 선정산내역 헬퍼 함수 및 상수
 */
import React from 'react';
import {
    TableColumn,
    FilterValidation,
    shouldShowColumn as baseShouldShowColumn,
    getColumnAlignment as baseGetColumnAlignment,
    validateDateRange,
    calculateDateRange
} from '../../../shared/lib/settlementBaseHelpers';
import { RiderPreSettlement } from '../types/riderPreSettlement.types';

// 타입 재내보내기
export type { TableColumn, FilterValidation };

/**
 * 금액 포맷팅 함수 (null/undefined 안전 처리)
 */
export const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('ko-KR').format(value);
};

/**
 * 테이블 컬럼 목록
 */
export const TABLE_COLUMNS: TableColumn[] = [
    { index: 1, header: 'NO', width: '60px', minWidth: '60px' },
    { index: 2, header: '협력사명', width: '120px', minWidth: '120px' },
    { index: 3, header: '라이더ID', width: '120px', minWidth: '120px' },
    { index: 4, header: '라이더명', width: '100px', minWidth: '100px' },
    { index: 5, header: '라이더구분', width: '100px', minWidth: '100px' },
    { index: 6, header: '배송일자', width: '120px', minWidth: '120px', tooltip: '수행일자\n지급일 = 배송일자 + 1일' },
    { index: 7, header: '지급일', width: '120px', minWidth: '120px' },
    { index: 8, header: '처리건수', width: '100px', minWidth: '100px' },
    { index: 9, header: '배달료', width: '120px', minWidth: '120px' },
    { index: 10, header: '중개사수수료', width: '140px', minWidth: '140px' },
    { index: 11, header: '협력사수수료', width: '140px', minWidth: '140px' },
    { index: 12, header: '총운영수수료', width: '140px', minWidth: '140px' },
    { index: 13, header: '과세배달료', width: '120px', minWidth: '120px', tooltip: '배달료(원단위 절삭) - 총운영수수료\n※ 선정산은 원단위 절삭 적용' },
    { index: 14, header: '월보수액', width: '120px', minWidth: '120px' },
    { index: 15, header: '라이더_고용', width: '120px', minWidth: '120px', tooltip: '월보수액 × 0.8% (반올림)\n※ 지점장, 만65세이상은 0원' },
    { index: 16, header: '라이더_산재', width: '120px', minWidth: '120px', tooltip: '월보수액 × 0.88% (반올림)\n※ 지점장만 0원' },
    { index: 17, header: '시간제보험료', width: '120px', minWidth: '120px', tooltip: '시간제보험 사용시 특정비용 일괄 차감\n(예: 근무시 8,000원 임의차감)' },
    { index: 18, header: '총합', width: '100px', minWidth: '100px', tooltip: '라이더_고용 + 라이더_산재 + 시간제보험료' },
    { index: 19, header: '소득세', width: '100px', minWidth: '100px' },
    { index: 20, header: '지방세', width: '100px', minWidth: '100px' },
    { index: 21, header: '원천징수', width: '120px', minWidth: '120px', tooltip: '소득세 + 지방세' },
    { index: 22, header: '본사 일차감', width: '120px', minWidth: '120px' },
    { index: 23, header: '협력사 일차감', width: '120px', minWidth: '120px' },
    { index: 24, header: '미수금', width: '120px', minWidth: '120px', tooltip: '선정산 당시 라이더의 잔여 미수금 잔액 (음수: 미상환 잔액)' },
    { index: 25, header: '선지급배달료', width: '140px', minWidth: '140px', tooltip: '과세배달료 - 총합 - 원천징수 - (전날 미수금 - 미수금)' },
];

/** 라이더 사용자에게 표시되는 컬럼 인덱스 목록 */
export const RIDER_VISIBLE_COLUMNS = [2, 3, 4, 5, 6, 7, 8, 11, 12, 13, 14, 15, 16, 18, 19, 21] as const;

/** 금액 컬럼 인덱스 (우측 정렬) */
export const CURRENCY_COLUMNS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25] as const;

/** 중앙 정렬 컬럼 인덱스 */
export const CENTER_ALIGN_COLUMNS = [1, 3, 5, 6, 7, 8] as const;

/**
 * 권한별 컬럼 표시 여부 확인
 */
export const shouldShowColumn = (columnIndex: number, isRiderUser: boolean): boolean => {
    return baseShouldShowColumn(columnIndex, RIDER_VISIBLE_COLUMNS, isRiderUser);
};

/**
 * 컬럼 정렬 스타일 반환
 */
export const getColumnAlignment = (columnIndex: number): React.CSSProperties => {
    return baseGetColumnAlignment(columnIndex, CENTER_ALIGN_COLUMNS, CURRENCY_COLUMNS);
};

/**
 * 필터 유효성 검사
 */
export const validateFilters = (
    acctStDate: string,
    acctEdDate: string,
    _supplierId: string
): FilterValidation => {
    return validateDateRange(acctStDate, acctEdDate);
};

/**
 * 초기 날짜 범위 계산 (종료일 기준 7일)
 */
export const calculateInitialDateRange = (endDate: string): { startDate: string; endDate: string } => {
    return calculateDateRange(endDate, 6);
};

/**
 * 컬럼 인덱스로 선정산 데이터 필드 값 반환
 * 정렬(useColumnSort)과 테이블 렌더링(SettlementTable) 양쪽에서 공통 사용.
 * 컬럼 1(NO)은 UI 전용 순번이므로 포함하지 않음.
 */
export const getPreSettlementValueByIndex = (
    settlement: RiderPreSettlement,
    colIndex: number
): string | number => {
    switch (colIndex) {
        case 2: return settlement.supplierName;
        case 3: return settlement.riderId;
        case 4: return settlement.riderName;
        case 5: return settlement.riderType;
        case 6: return settlement.deliveryDate;
        case 7: return settlement.paymentDate;
        case 8: return settlement.processCount;
        case 9: return settlement.deliveryFee;
        case 10: return settlement.brokerManageFee;
        case 11: return settlement.partnerOperationFee;
        case 12: return settlement.totalOperationFee;
        case 13: return settlement.taxableDeliveryFee;
        case 14: return settlement.monthlySalary;
        case 15: return settlement.riderEmployment;
        case 16: return settlement.riderIndustrial;
        case 17: return settlement.hourlyInsurance;
        case 18: return settlement.insuranceTotal;
        case 19: return settlement.incomeTax;
        case 20: return settlement.localTax;
        case 21: return settlement.withholdingTax;
        case 22: return settlement.hdcDeductVal;
        case 23: return settlement.sdcDeductVal;
        case 24: return settlement.unpaidAmt;
        case 25: return settlement.advanceSettlement;
        default: return 0;
    }
};
